import { createTheme } from '@mui/material/styles';

// Bitscale VideoMeet color palette
const bitscaleColors = {
  primary: {
    main: '#0073a8',
    dark: '#005075',
    light: '#3391bc',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#9b51e0',
    dark: '#7b3eb3',
    light: '#af74e6',
    contrastText: '#ffffff',
  },
  background: {
    default: '#0a0a0f',
    paper: '#111118',
    elevated: '#1a1a24',
  },
  surface: {
    main: '#1a1a2e',
    light: '#252538',
    dark: '#12121c',
  },
  text: {
    primary: '#ffffff',
    secondary: '#a0a0b0',
    disabled: '#606070',
  },
  success: {
    main: '#00d084',
    dark: '#00a86b',
    light: '#33d99d',
  },
  error: {
    main: '#cf2e2e',
    dark: '#a62424',
    light: '#d85858',
  },
  warning: {
    main: '#fcb900',
    dark: '#c99400',
    light: '#fdc733',
  },
  info: {
    main: '#0693e3',
    dark: '#0575b5',
    light: '#38a9e9',
  },
  gradient: {
    primary: 'linear-gradient(135deg, #0693e3 0%, #9b51e0 100%)',
    success: 'linear-gradient(135deg, #7adcb4 0%, #00d084 100%)',
    warm: 'linear-gradient(135deg, #ff6900 0%, #cf2e2e 100%)',
  },
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: bitscaleColors.primary,
    secondary: bitscaleColors.secondary,
    background: bitscaleColors.background,
    text: bitscaleColors.text,
    success: bitscaleColors.success,
    error: bitscaleColors.error,
    warning: bitscaleColors.warning,
    info: bitscaleColors.info,
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '0.875rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: bitscaleColors.background.default,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: bitscaleColors.background.paper,
          },
          '&::-webkit-scrollbar-thumb': {
            background: bitscaleColors.surface.light,
            borderRadius: '4px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.9375rem',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 115, 168, 0.3)',
          },
        },
        containedPrimary: {
          background: bitscaleColors.gradient.primary,
          '&:hover': {
            background: bitscaleColors.gradient.primary,
            filter: 'brightness(1.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: bitscaleColors.background.paper,
          borderRadius: 16,
          border: '1px solid rgba(255, 255, 255, 0.05)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: bitscaleColors.surface.dark,
            borderRadius: 8,
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: bitscaleColors.primary.main,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: bitscaleColors.background.elevated,
          borderRadius: 16,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: bitscaleColors.surface.main,
          fontSize: '0.8125rem',
          borderRadius: 8,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: bitscaleColors.primary.main,
        },
      },
    },
  },
});

export { bitscaleColors };
export default theme;
